import React, { useState } from 'react';
import styles from './VariantModal.module.css';
import NoImage from '../../img/noinage.png';
import {
    IoColorPalette,
    IoResize,
    IoClose,
    IoAdd,
    IoCamera,
    IoImage,
} from 'react-icons/io5';
import { handleFieldChange, handlePriceChange } from './HelperFunction';
import AltTagModal from './sections/AltTagModal';
import { toast } from 'react-toastify';

function VariantModal({
    isOpen,
    onClose,
    variationType,
    variationColorN,
    setVariationColorN,
    rows,
    setRows,
    rowsSize,
    addRow,
    addRowSize,
    handleInputChange,
    handleImageChange,
    handleRemove,
    handleInputChangeSize,
    handleImageChangeSize,
    handleRemoveSize,
    handleRemoveColorSize,
    setRowsSize,
    errorFields,
    setErrorFields,
    onUpdateVariantAltText,
}) {
    const [altModalOpen, setAltModalOpen] = useState(false);
    const [selectedVariantIndex, setSelectedVariantIndex] = useState(null);
    const [selectedVariantType, setSelectedVariantType] = useState(null); // 'color' or 'colorSize'
    const [variantErrors, setVariantErrors] = useState({});

    const handleUpdateAltText = (variantIndex, altText) => {
        onUpdateVariantAltText(variantIndex, altText, selectedVariantType);
    };

    // Handle price change for Color variants - update only the selling price field
    const handleColorPriceChange = (event, index) => {
        const { name, value } = event.target;
        const updatedRows = [...rows];
        updatedRows[index][name] = value;
        setRows(updatedRows);
    };

    // Auto-populate compare and cost price when user finishes typing selling price
    const handleColorPriceBlur = (event, index) => {
        const { value } = event.target;
        const updatedRows = [...rows];
        const row = updatedRows[index];

        // Only auto-populate if the fields are empty OR still have the old auto-populated value
        // Don't overwrite if user manually changed them
        if (value) {
            // Auto-populate Compare Price only if it's empty or was auto-populated before
            if (!row.PriceCompare || row.PriceCompare === '' || row.PriceCompare === row.price) {
                updatedRows[index].PriceCompare = value;
            }
            // Auto-populate Cost Price only if it's empty or was auto-populated before
            if (!row.buy_price || row.buy_price === '' || row.buy_price === row.price) {
                updatedRows[index].buy_price = value;
            }
            setRows(updatedRows);
        }
    };

    // Handle price change for Color+Size variants - update only the selling price field
    const handleSizePriceChange = (event, colorIndex, sizeIndex) => {
        const { name, value } = event.target;
        const updatedRows = [...rowsSize];
        updatedRows[colorIndex].sizes[sizeIndex][name] = value;
        setRowsSize(updatedRows);
    };

    // Auto-populate compare and cost price when user finishes typing selling price
    const handleSizePriceBlur = (event, colorIndex, sizeIndex) => {
        const { value } = event.target;
        const updatedRows = [...rowsSize];
        const size = updatedRows[colorIndex].sizes[sizeIndex];

        // Only auto-populate if the fields are empty OR still have the old auto-populated value
        // Don't overwrite if user manually changed them
        if (value) {
            // Auto-populate Compare Price only if it's empty or was auto-populated before
            if (!size.PriceCompare || size.PriceCompare === '' || size.PriceCompare === size.price) {
                updatedRows[colorIndex].sizes[sizeIndex].PriceCompare = value;
            }
            // Auto-populate Cost Price only if it's empty or was auto-populated before
            if (!size.buy_price || size.buy_price === '' || size.buy_price === size.price) {
                updatedRows[colorIndex].sizes[sizeIndex].buy_price = value;
            }
            setRowsSize(updatedRows);
        }
    };

    const validateVariants = () => {
        const errors = {};
        let hasErrors = false;

        if (variationType === 'color') {
            rows.forEach((row, index) => {
                const rowErrors = {};
                if (!row.image) {
                    rowErrors.image = true;
                    hasErrors = true;
                }
                // Selling price is REQUIRED, buy price and compare price are optional
                if (!row.price || parseFloat(row.price) <= 0) {
                    rowErrors.price = true;
                    hasErrors = true;
                }
                // Buy price and compare price are optional (will default to selling price)
                if (row.quantity === '' || row.quantity === null || row.quantity === undefined || isNaN(Number(row.quantity)) || Number(row.quantity) < 0) {
                    rowErrors.quantity = true;
                    hasErrors = true;
                }
                if (!row.ShipFrom || row.ShipFrom.trim() === '') {
                    rowErrors.ShipFrom = true;
                    hasErrors = true;
                }
                if (!row.DeliveryTime || row.DeliveryTime.trim() === '') {
                    rowErrors.DeliveryTime = true;
                    hasErrors = true;
                }
                if (Object.keys(rowErrors).length > 0) {
                    errors[`color_${index}`] = rowErrors;
                }
            });
        } else if (variationType === 'colorSize') {
            rowsSize.forEach((colorVariant, colorIndex) => {
                const colorErrors = {};
                if (!colorVariant.colorImage) {
                    colorErrors.image = true;
                    hasErrors = true;
                }

                colorVariant.sizes.forEach((size, sizeIndex) => {
                    const sizeErrors = {};
                    if (!size.sizeName || size.sizeName.trim() === '') {
                        sizeErrors.sizeName = true;
                        hasErrors = true;
                    }
                    // Selling price is REQUIRED, buy price and compare price are optional
                    if (!size.price || parseFloat(size.price) <= 0) {
                        sizeErrors.price = true;
                        hasErrors = true;
                    }
                    // Buy price and compare price are optional (will default to selling price)
                    if (size.quantity === '' || size.quantity === null || size.quantity === undefined || isNaN(Number(size.quantity)) || Number(size.quantity) < 0) {
                        sizeErrors.quantity = true;
                        hasErrors = true;
                    }
                    if (!size.ShipFrom || size.ShipFrom.trim() === '') {
                        sizeErrors.ShipFrom = true;
                        hasErrors = true;
                    }
                    if (!size.DeliveryTime || size.DeliveryTime.trim() === '') {
                        sizeErrors.DeliveryTime = true;
                        hasErrors = true;
                    }
                    if (Object.keys(sizeErrors).length > 0) {
                        if (!errors[`colorSize_${colorIndex}`]) {
                            errors[`colorSize_${colorIndex}`] = {};
                        }
                        errors[`colorSize_${colorIndex}`][`size_${sizeIndex}`] = sizeErrors;
                    }
                });

                if (Object.keys(colorErrors).length > 0) {
                    if (!errors[`colorSize_${colorIndex}`]) {
                        errors[`colorSize_${colorIndex}`] = {};
                    }
                    errors[`colorSize_${colorIndex}`].colorErrors = colorErrors;
                }
            });
        }

        setVariantErrors(errors);
        return !hasErrors;
    };

    const handleDoneClick = () => {
        if (validateVariants()) {
            onClose();
        } else {
            toast.error('Please fill in all required fields: Image, Size, Selling Price, Quantity, Ship From, and Delivery Time for all variants');

            // Scroll to first error field
            setTimeout(() => {
                // Find first error field by searching for elements with error classes
                const errorSelectors = [
                    `[class*="field-error"]`,
                    `[class*="image-error"]`
                ];

                for (const selector of errorSelectors) {
                    const firstError = document.querySelector(selector);
                    if (firstError) {
                        firstError.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                            inline: 'nearest'
                        });
                        break;
                    }
                }
            }, 100);
        }
    };

    const handleClose = () => {
        // Remove variants that don't have required fields when closing without Done
        // Buy price and compare price are optional (selling price is required)
        if (variationType === 'color') {
            const validRows = rows.filter(row => {
                return row.image &&
                       row.price && parseFloat(row.price) > 0 &&
                       row.quantity !== '' && row.quantity !== null && row.quantity !== undefined && !isNaN(Number(row.quantity)) && Number(row.quantity) >= 0 &&
                       row.ShipFrom && row.ShipFrom.trim() !== '' &&
                       row.DeliveryTime && row.DeliveryTime.trim() !== '';
            });

            // Update rows to only keep valid ones
            if (validRows.length !== rows.length) {
                setRows(validRows);
            }
        } else if (variationType === 'colorSize') {
            const validRowsSize = rowsSize.map(colorVariant => {
                // Check if color has image and at least one valid size
                if (!colorVariant.colorImage) return null;

                return {
                    ...colorVariant,
                    sizes: colorVariant.sizes.filter(size => {
                        return size.sizeName && size.sizeName.trim() !== '' &&
                               size.price && parseFloat(size.price) > 0 &&
                               size.quantity !== '' && size.quantity !== null && size.quantity !== undefined && !isNaN(Number(size.quantity)) && Number(size.quantity) >= 0 &&
                               size.ShipFrom && size.ShipFrom.trim() !== '' &&
                               size.DeliveryTime && size.DeliveryTime.trim() !== '';
                    })
                };
            }).filter(colorVariant => colorVariant !== null && colorVariant.sizes.length > 0);

            // Update rowsSize to only keep valid ones
            if (validRowsSize.length !== rowsSize.length ||
                validRowsSize.some((variant, idx) => variant.sizes.length !== rowsSize[idx]?.sizes?.length)) {
                setRowsSize(validRowsSize);
            }
        }

        // Clear validation errors
        setVariantErrors({});

        // Close modal
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className={styles['modal-overlay']} onClick={handleClose}>
            <div
                className={styles['modal-container']}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Modal Header */}
                <div className={styles['modal-header']}>
                    <div className={styles['modal-header-content']}>
                        <div className={styles['modal-icon']}>
                            {variationType === 'color' ? (
                                <IoColorPalette />
                            ) : (
                                <IoResize />
                            )}
                        </div>
                        <div>
                            <h2 className={styles['modal-title']}>
                                {variationType === 'color'
                                    ? 'Manage Color Variants'
                                    : 'Manage Color & Size Variants'}
                            </h2>
                            <p className={styles['modal-subtitle']}>
                                {variationType === 'color'
                                    ? 'Add and configure different color options for your product'
                                    : 'Add colors and sizes with individual pricing and stock'}
                            </p>
                        </div>
                    </div>
                    <button
                        className={styles['modal-close']}
                        onClick={handleClose}
                        aria-label="Close modal"
                    >
                        <IoClose />
                    </button>
                </div>

                {/* Modal Body */}
                <div className={styles['modal-body']}>
                    {/* Add Variant Section */}
                    <div className={styles['add-variant-section']}>
                        <div className={styles['input-group']}>
                            <input
                                type="text"
                                className={styles['variant-input']}
                                placeholder="Enter color name (e.g., Red, Blue, Black)"
                                value={variationColorN}
                                onChange={handleFieldChange(
                                    'variationColorN',
                                    setVariationColorN,
                                    errorFields,
                                    setErrorFields
                                )}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' && variationColorN) {
                                        variationType === 'color'
                                            ? addRow()
                                            : addRowSize();
                                    }
                                }}
                            />
                            <button
                                className={styles['add-button']}
                                onClick={() => {
                                    if (variationColorN) {
                                        variationType === 'color'
                                            ? addRow()
                                            : addRowSize();
                                    }
                                }}
                            >
                                <IoAdd className={styles['add-icon']} />
                                Add Color
                            </button>
                        </div>
                    </div>

                    {/* Variants List */}
                    <div className={styles['variants-list']}>
                        {variationType === 'color' ? (
                            // Color Variants
                            rows.length > 0 ? (
                                rows.map((row, index) => (
                                    <div
                                        key={index}
                                        className={styles['variant-card']}
                                    >
                                        <div
                                            className={
                                                styles['variant-card-header']
                                            }
                                        >
                                            <div
                                                className={`${
                                                    styles['variant-image-wrapper']
                                                } ${
                                                    variantErrors[`color_${index}`]?.image
                                                        ? styles['image-error']
                                                        : ''
                                                }`}
                                            >
                                                <img
                                                    src={
                                                        row.image
                                                            ? row.image
                                                            : NoImage
                                                    }
                                                    alt={
                                                        row.altText ||
                                                        row.colorName
                                                    }
                                                    className={
                                                        styles['variant-image']
                                                    }
                                                />
                                                <label
                                                    className={
                                                        styles[
                                                            'image-upload-label'
                                                        ]
                                                    }
                                                >
                                                    <input
                                                        type="file"
                                                        className={
                                                            styles[
                                                                'file-input-hidden'
                                                            ]
                                                        }
                                                        onChange={(event) =>
                                                            handleImageChange(
                                                                event,
                                                                index
                                                            )
                                                        }
                                                        accept="image/*"
                                                    />
                                                    <IoCamera />
                                                </label>
                                                {row.image && (
                                                    <button
                                                        onClick={() => {
                                                            setSelectedVariantIndex(
                                                                index
                                                            );
                                                            setSelectedVariantType(
                                                                'color'
                                                            );
                                                            setAltModalOpen(
                                                                true
                                                            );
                                                        }}
                                                        className={
                                                            styles[
                                                                'alt-text-button'
                                                            ]
                                                        }
                                                        type="button"
                                                        title="Edit alt text for SEO"
                                                    >
                                                        <IoImage />
                                                        Alt
                                                    </button>
                                                )}
                                            </div>
                                            <div
                                                className={
                                                    styles['variant-name-badge']
                                                }
                                            >
                                                {row.colorName}
                                            </div>
                                            <button
                                                className={
                                                    styles['remove-button']
                                                }
                                                onClick={() =>
                                                    handleRemove(index)
                                                }
                                            >
                                                <IoClose />
                                            </button>
                                        </div>

                                        <div
                                            className={
                                                styles['variant-fields-grid']
                                            }
                                        >
                                            <div
                                                className={
                                                    styles['field-wrapper']
                                                }
                                            >
                                                <label
                                                    className={
                                                        styles['field-label']
                                                    }
                                                >
                                                    Selling Price
                                                </label>
                                                <input
                                                    type="text"
                                                    name="price"
                                                    className={`${styles['field-input']} ${
                                                        variantErrors[`color_${index}`]?.price
                                                            ? styles['field-error']
                                                            : ''
                                                    }`}
                                                    placeholder="Selling price"
                                                    value={row.price}
                                                    onChange={(event) =>
                                                        handleColorPriceChange(
                                                            event,
                                                            index
                                                        )
                                                    }
                                                    onBlur={(event) =>
                                                        handleColorPriceBlur(
                                                            event,
                                                            index
                                                        )
                                                    }
                                                />
                                            </div>

                                            <div
                                                className={
                                                    styles['field-wrapper']
                                                }
                                            >
                                                <label
                                                    className={
                                                        styles['field-label']
                                                    }
                                                >
                                                    Compare Price (Optional)
                                                </label>
                                                <input
                                                    type="text"
                                                    name="PriceCompare"
                                                    className={styles['field-input']}
                                                    placeholder="Optional - defaults to selling price"
                                                    value={row.PriceCompare}
                                                    onChange={(event) =>
                                                        handleInputChange(
                                                            event,
                                                            index
                                                        )
                                                    }
                                                />
                                            </div>

                                            <div
                                                className={
                                                    styles['field-wrapper']
                                                }
                                            >
                                                <label
                                                    className={
                                                        styles['field-label']
                                                    }
                                                >
                                                    Cost Price (Optional)
                                                </label>
                                                <input
                                                    type="text"
                                                    name="buy_price"
                                                    className={styles['field-input']}
                                                    placeholder="Optional - defaults to selling price"
                                                    value={row.buy_price}
                                                    onChange={(event) =>
                                                        handleInputChange(
                                                            event,
                                                            index
                                                        )
                                                    }
                                                />
                                            </div>

                                            <div
                                                className={
                                                    styles['field-wrapper']
                                                }
                                            >
                                                <label
                                                    className={
                                                        styles['field-label']
                                                    }
                                                >
                                                    Quantity
                                                </label>
                                                <input
                                                    type="text"
                                                    name="quantity"
                                                    className={`${styles['field-input']} ${
                                                        variantErrors[`color_${index}`]?.quantity
                                                            ? styles['field-error']
                                                            : ''
                                                    }`}
                                                    placeholder="0"
                                                    value={row.quantity}
                                                    onChange={(event) =>
                                                        handleInputChange(
                                                            event,
                                                            index
                                                        )
                                                    }
                                                />
                                            </div>

                                            <div
                                                className={
                                                    styles['field-wrapper']
                                                }
                                            >
                                                <label
                                                    className={
                                                        styles['field-label']
                                                    }
                                                >
                                                    Ship From
                                                </label>
                                                <input
                                                    type="text"
                                                    name="ShipFrom"
                                                    className={`${styles['field-input']} ${
                                                        variantErrors[`color_${index}`]?.ShipFrom
                                                            ? styles['field-error']
                                                            : ''
                                                    }`}
                                                    placeholder="Location"
                                                    value={row.ShipFrom}
                                                    onChange={(event) =>
                                                        handleInputChange(
                                                            event,
                                                            index
                                                        )
                                                    }
                                                />
                                            </div>

                                            <div
                                                className={
                                                    styles['field-wrapper']
                                                }
                                            >
                                                <label
                                                    className={
                                                        styles['field-label']
                                                    }
                                                >
                                                    Delivery Time
                                                </label>
                                                <input
                                                    type="text"
                                                    name="DeliveryTime"
                                                    className={`${styles['field-input']} ${
                                                        variantErrors[`color_${index}`]?.DeliveryTime
                                                            ? styles['field-error']
                                                            : ''
                                                    }`}
                                                    placeholder="e.g., 2-3 days"
                                                    value={row.DeliveryTime}
                                                    onChange={(event) =>
                                                        handleInputChange(
                                                            event,
                                                            index
                                                        )
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className={styles['empty-state']}>
                                    <div className={styles['empty-icon']}>
                                        <IoColorPalette />
                                    </div>
                                    <p className={styles['empty-text']}>
                                        No color variants added yet
                                    </p>
                                    <p className={styles['empty-subtext']}>
                                        Add your first color variant above to
                                        get started
                                    </p>
                                </div>
                            )
                        ) : // Color + Size Variants
                        rowsSize.length > 0 ? (
                            rowsSize.map((row, colorIndex) => (
                                <div
                                    key={colorIndex}
                                    className={styles['variant-card-size']}
                                >
                                    <div className={styles['size-card-header']}>
                                        <div
                                            className={`${
                                                styles['size-image-wrapper']
                                            } ${
                                                variantErrors[`colorSize_${colorIndex}`]?.colorErrors?.image
                                                    ? styles['image-error']
                                                    : ''
                                            }`}
                                        >
                                            <img
                                                src={
                                                    row.colorImage
                                                        ? row.colorImage
                                                        : NoImage
                                                }
                                                alt={
                                                    row.altText || row.colorName
                                                }
                                                className={
                                                    styles['size-variant-image']
                                                }
                                            />
                                            <label
                                                className={
                                                    styles[
                                                        'image-upload-label-size'
                                                    ]
                                                }
                                            >
                                                <input
                                                    type="file"
                                                    className={
                                                        styles[
                                                            'file-input-hidden'
                                                        ]
                                                    }
                                                    onChange={(event) =>
                                                        handleImageChangeSize(
                                                            event,
                                                            colorIndex
                                                        )
                                                    }
                                                    accept="image/*"
                                                />
                                                <IoCamera />
                                            </label>
                                            {row.colorImage && (
                                                <button
                                                    onClick={() => {
                                                        setSelectedVariantIndex(
                                                            colorIndex
                                                        );
                                                        setSelectedVariantType(
                                                            'colorSize'
                                                        );
                                                        setAltModalOpen(true);
                                                    }}
                                                    className={
                                                        styles[
                                                            'alt-text-button'
                                                        ]
                                                    }
                                                    type="button"
                                                    title="Edit alt text for SEO"
                                                >
                                                    <IoImage />
                                                    Alt
                                                </button>
                                            )}
                                        </div>
                                        <div
                                            className={
                                                styles[
                                                    'size-variant-name-badge'
                                                ]
                                            }
                                        >
                                            {row.colorName}
                                        </div>
                                        <button
                                            className={styles['remove-button']}
                                            onClick={() =>
                                                handleRemoveColorSize(
                                                    colorIndex
                                                )
                                            }
                                        >
                                            <IoClose />
                                        </button>
                                    </div>

                                    <div className={styles['sizes-container']}>
                                        {row.sizes.map((size, sizeIndex) => (
                                            <div
                                                key={sizeIndex}
                                                className={styles['size-row']}
                                            >
                                                <input
                                                    type="text"
                                                    name="sizeName"
                                                    className={`${styles['size-input']} ${
                                                        variantErrors[`colorSize_${colorIndex}`]?.[`size_${sizeIndex}`]?.sizeName
                                                            ? styles['field-error']
                                                            : ''
                                                    }`}
                                                    placeholder="Size (S, M, L)"
                                                    value={size.sizeName}
                                                    onChange={(event) =>
                                                        handleInputChangeSize(
                                                            event,
                                                            colorIndex,
                                                            sizeIndex
                                                        )
                                                    }
                                                />
                                                <input
                                                    type="text"
                                                    name="price"
                                                    className={`${styles['size-input']} ${
                                                        variantErrors[`colorSize_${colorIndex}`]?.[`size_${sizeIndex}`]?.price
                                                            ? styles['field-error']
                                                            : ''
                                                    }`}
                                                    placeholder="Selling price"
                                                    value={size.price}
                                                    onChange={(event) =>
                                                        handleSizePriceChange(
                                                            event,
                                                            colorIndex,
                                                            sizeIndex
                                                        )
                                                    }
                                                    onBlur={(event) =>
                                                        handleSizePriceBlur(
                                                            event,
                                                            colorIndex,
                                                            sizeIndex
                                                        )
                                                    }
                                                />
                                                <input
                                                    type="text"
                                                    name="buy_price"
                                                    className={styles['size-input']}
                                                    placeholder="Cost price (Optional)"
                                                    value={size.buy_price}
                                                    onChange={(event) =>
                                                        handleInputChangeSize(
                                                            event,
                                                            colorIndex,
                                                            sizeIndex
                                                        )
                                                    }
                                                />
                                                <input
                                                    type="text"
                                                    name="quantity"
                                                    className={`${styles['size-input']} ${
                                                        variantErrors[`colorSize_${colorIndex}`]?.[`size_${sizeIndex}`]?.quantity
                                                            ? styles['field-error']
                                                            : ''
                                                    }`}
                                                    placeholder="Quantity"
                                                    value={size.quantity}
                                                    onChange={(event) =>
                                                        handleInputChangeSize(
                                                            event,
                                                            colorIndex,
                                                            sizeIndex
                                                        )
                                                    }
                                                />
                                                <input
                                                    type="text"
                                                    name="PriceCompare"
                                                    className={styles['size-input']}
                                                    placeholder="Compare Price (Optional)"
                                                    value={size.PriceCompare || ''}
                                                    onChange={(event) =>
                                                        handleInputChangeSize(
                                                            event,
                                                            colorIndex,
                                                            sizeIndex
                                                        )
                                                    }
                                                />
                                                <input
                                                    type="text"
                                                    name="ShipFrom"
                                                    className={`${styles['size-input']} ${
                                                        variantErrors[`colorSize_${colorIndex}`]?.[`size_${sizeIndex}`]?.ShipFrom
                                                            ? styles['field-error']
                                                            : ''
                                                    }`}
                                                    placeholder="Ship From"
                                                    value={size.ShipFrom || ''}
                                                    onChange={(event) =>
                                                        handleInputChangeSize(
                                                            event,
                                                            colorIndex,
                                                            sizeIndex
                                                        )
                                                    }
                                                />
                                                <input
                                                    type="text"
                                                    name="DeliveryTime"
                                                    className={`${styles['size-input']} ${
                                                        variantErrors[`colorSize_${colorIndex}`]?.[`size_${sizeIndex}`]?.DeliveryTime
                                                            ? styles['field-error']
                                                            : ''
                                                    }`}
                                                    placeholder="Delivery Time"
                                                    value={size.DeliveryTime || ''}
                                                    onChange={(event) =>
                                                        handleInputChangeSize(
                                                            event,
                                                            colorIndex,
                                                            sizeIndex
                                                        )
                                                    }
                                                />
                                                <button
                                                    className={
                                                        styles[
                                                            'size-remove-button'
                                                        ]
                                                    }
                                                    onClick={() =>
                                                        handleRemoveSize(
                                                            colorIndex,
                                                            sizeIndex
                                                        )
                                                    }
                                                >
                                                    <IoClose />
                                                </button>
                                            </div>
                                        ))}
                                        <button
                                            className={
                                                styles['add-size-button']
                                            }
                                            onClick={() => {
                                                const newSize = {
                                                    sizeName: '',
                                                    price: '',
                                                    buy_price: '',
                                                    quantity: '',
                                                    PriceCompare: '',
                                                    ShipFrom: 'Dhaka',
                                                    DeliveryTime: '1-3',
                                                    gid: Math.floor(
                                                        Math.random() *
                                                            9000000976
                                                    ),
                                                };
                                                const updatedRows = [
                                                    ...rowsSize,
                                                ];
                                                updatedRows[
                                                    colorIndex
                                                ].sizes.push(newSize);
                                                setRowsSize(updatedRows);
                                            }}
                                        >
                                            + Add Size
                                        </button>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className={styles['empty-state']}>
                                <div className={styles['empty-icon']}>
                                    <IoResize />
                                </div>
                                <p className={styles['empty-text']}>
                                    No color & size variants added yet
                                </p>
                                <p className={styles['empty-subtext']}>
                                    Add your first color variant above to get
                                    started
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Modal Footer */}
                <div className={styles['modal-footer']}>
                    <div className={styles['footer-info']}>
                        <span className={styles['variant-count']}>
                            {variationType === 'color'
                                ? `${rows.length} variant${
                                      rows.length !== 1 ? 's' : ''
                                  }`
                                : `${rowsSize.length} color${
                                      rowsSize.length !== 1 ? 's' : ''
                                  }`}
                        </span>
                    </div>
                    <button className={styles['done-button']} onClick={handleDoneClick}>
                        Done
                    </button>
                </div>
            </div>

            {/* Alt Tag Modal */}
            <AltTagModal
                isOpen={altModalOpen}
                onClose={() => {
                    setAltModalOpen(false);
                    setSelectedVariantIndex(null);
                    setSelectedVariantType(null);
                }}
                image={
                    selectedVariantIndex !== null &&
                    selectedVariantType === 'color'
                        ? {
                              url: rows[selectedVariantIndex]?.image,
                              altText: rows[selectedVariantIndex]?.altText,
                              colorName: rows[selectedVariantIndex]?.colorName,
                          }
                        : selectedVariantIndex !== null &&
                          selectedVariantType === 'colorSize'
                        ? {
                              url: rowsSize[selectedVariantIndex]?.colorImage,
                              altText: rowsSize[selectedVariantIndex]?.altText,
                              colorName:
                                  rowsSize[selectedVariantIndex]?.colorName,
                          }
                        : null
                }
                onSave={handleUpdateAltText}
                imageIndex={selectedVariantIndex}
            />
        </div>
    );
}

export default VariantModal;
